/**
 * Offline write queue — persists failed mutations to localStorage and replays
 * them when the network returns. Lightweight (no idb dependency).
 *
 * Public API:
 *   enqueue({ method, url, data, label }) → generated id
 *   flush()                                → replays pending ops; resolves with summary
 *   list()                                 → current queue (used by the UI indicator)
 *   remove(id)                             → drop a single entry
 *   clear()                                → wipe all
 *   subscribe(fn)                          → returns unsubscribe; fires on queue change
 *
 * The queue is intentionally simple: only whitelisted endpoints can be queued,
 * and each op has max 5 retry attempts. File uploads (FormData) are NOT queued
 * because blobs can't survive localStorage JSON serialisation.
 */

import API from "./api";

const STORAGE_KEY = "fixli_offline_queue_v1";
const MAX_ATTEMPTS = 5;

/** Endpoint patterns that are safe to retry offline. Exact prefix match. */
const QUEUEABLE_PATTERNS = [
  // Supplier / supplier-portal write actions (field worker priority)
  /^\/supplier-portal\/tasks\/[^/]+\/(en-route|arrived|start|complete)$/,
  /^\/supplier-portal\/work-orders\/[^/]+\/(en-route|arrived|start|complete)$/,
  // In-house operative job/emergency actions (arrive works offline; complete is
  // handled by offlinePhotoQueue because it depends on photos syncing first)
  /^\/in-house-portal\/[^/]+\/[^/]+\/arrive$/,
  // Admin quick-creates
  /^\/tasks$/,
  /^\/jobs$/,
  /^\/time-logs$/,
  /^\/notes$/,
];

export function isQueueable(method, url) {
  if ((method || "").toUpperCase() !== "POST") return false;
  const u = (url || "").replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "");
  return QUEUEABLE_PATTERNS.some((re) => re.test(u));
}

const listeners = new Set();

function _read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function _write(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota exceeded — best effort drop oldest */
    if (items.length > 1) _write(items.slice(1));
  }
  listeners.forEach((fn) => {
    try { fn(items); } catch { /* ignore */ }
  });
}

export function list() {
  return _read();
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(_read());
  return () => listeners.delete(fn);
}

export function enqueue({ method = "POST", url, data, label = "Queued action" }) {
  const id = `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const item = {
    id, method: method.toUpperCase(), url, data: data || {}, label,
    queued_at: new Date().toISOString(), attempts: 0, last_error: null,
  };
  const items = _read();
  items.push(item);
  _write(items);
  return id;
}

export function remove(id) {
  _write(_read().filter((i) => i.id !== id));
}

export function clear() {
  _write([]);
}

let flushing = false;

/**
 * Drain the queue by replaying each op through the main API axios instance.
 * Stops for an item after MAX_ATTEMPTS; network errors keep the item queued.
 */
export async function flush() {
  if (flushing || !navigator.onLine) return { attempted: 0, ok: 0, failed: 0 };
  flushing = true;
  let ok = 0;
  let failed = 0;
  let attempted = 0;
  try {
    /* eslint-disable no-await-in-loop */
    while (true) {
      const items = _read();
      if (items.length === 0) break;
      const item = items[0];
      attempted += 1;
      try {
        await API.request({
          method: item.method, url: item.url, data: item.data,
          headers: { "X-Queued-At": item.queued_at },
          timeout: 15000,
        });
        remove(item.id);
        ok += 1;
      } catch (err) {
        const status = err.response?.status;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          // Permanent failure — drop to avoid blocking queue
          remove(item.id);
          failed += 1;
        } else {
          // Transient — bump attempts, stop draining this round
          const updated = _read().map((x) =>
            x.id === item.id
              ? { ...x, attempts: x.attempts + 1, last_error: err.message || "network" }
              : x,
          );
          _write(updated);
          const current = updated.find((x) => x.id === item.id);
          if (current && current.attempts >= MAX_ATTEMPTS) {
            remove(item.id);
            failed += 1;
          } else {
            break;
          }
        }
      }
    }
    /* eslint-enable no-await-in-loop */
  } finally {
    flushing = false;
  }
  return { attempted, ok, failed };
}

// Auto-flush when browser comes online
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flush(); });
  // Also flush once on load — if the last session left ops
  if (navigator.onLine) setTimeout(() => flush(), 1500);
}

export default { enqueue, flush, list, remove, clear, subscribe, isQueueable };
